import React from 'react';
import { X, FileDown, Loader2 } from 'lucide-react';
import { Book } from '../lib/database.types';
import { exportBook, downloadExport } from '../lib/export/index';
import type { ExportFormat } from '../lib/export/types';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  book: Book;
}

const exportFormats: { id: ExportFormat; label: string; description: string; note?: string }[] = [
  {
    id: 'kdp-epub',
    label: 'KDP-Ready EPUB',
    description: 'Kindle upload-ready EPUB format',
    note: 'EPUB 3.0 format. We recommend validating with EPUBCheck (epubcheck.org) before uploading to Amazon.'
  },
  {
    id: 'kdp-pdf',
    label: 'KDP-Ready Print PDF',
    description: '6x9 trim size, mirrored margins, ready for Amazon upload',
    note: '6x9 trim, dynamic gutter margins based on page count. Verify with KDP previewer before publishing.'
  },
  {
    id: 'lead-magnet-pdf',
    label: 'Lead Magnet PDF',
    description: 'Branded layout, US Letter format, styled for marketing'
  },
  {
    id: 'docx',
    label: 'Microsoft Word (DOCX)',
    description: 'Editable Microsoft Word document'
  }
];

export function ExportDialog({ isOpen, onClose, book }: ExportDialogProps) {
  const [exporting, setExporting] = React.useState<ExportFormat | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Close on Escape key
  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleExport = async (format: ExportFormat) => {
    setExporting(format);
    setError(null);

    try {
      const result = await exportBook({
        format,
        book,
        includeTableOfContents: true,
        includeCopyright: true,
        includeAuthorBio: true
      });
      downloadExport(result);

      
    } catch (err) {
      console.error('Export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export book');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Export Book
              </h3>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Export failed</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-4">
                {exportFormats.map((format) => (
                  <button
                    key={format.id}
                    onClick={() => handleExport(format.id)}
                    disabled={!!exporting}
                    className="w-full flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  >
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{format.label}</h4>
                      <p className="mt-1 text-sm text-gray-500">{format.description}</p>
                      {format.note && (
                        <p className="mt-1 text-xs text-gray-400">{format.note}</p>
                      )}
                    </div>
                    {exporting === format.id ? (
                      <Loader2 className="h-5 w-5 text-indigo-600 animate-spin flex-shrink-0 mt-1" />
                    ) : (
                      <FileDown className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                    )}
                  </button>
                ))}

                {book.chapters?.some(ch => ch.chapter_references && ch.chapter_references.trim()) && (
                  <p className="text-xs text-gray-500 bg-gray-50 rounded-md p-3">
                    This book includes a Notes section with chapter references.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}